import { useState } from "react";
import { Link } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Compass, ArrowLeft, ArrowRight, Briefcase, GraduationCap, Building2, MapPin, Store, Users,
  ShoppingCart, Clock, Tag, Croissant, Shirt, Sofa, Hammer, Trophy, Sparkles, Plug, PawPrint,
} from "lucide-react";

const CATEGORIES = [
  { id: "all", label: "Tous" },
  { id: "alimentaire", label: "Alimentaire" },
  { id: "maison", label: "Équipement & maison" },
  { id: "specialise", label: "Spécialisé" },
];

const CAT_STYLE = {
  alimentaire: { chip: "bg-orange-100 text-orange-700", ic: "bg-orange-100 text-orange-600", ring: "hover:border-orange-300" },
  maison: { chip: "bg-sky-100 text-sky-700", ic: "bg-sky-100 text-sky-600", ring: "hover:border-sky-300" },
  specialise: { chip: "bg-emerald-100 text-emerald-700", ic: "bg-emerald-100 text-emerald-600", ring: "hover:border-emerald-300" },
};

const CAT_LABEL = { alimentaire: "Alimentaire", maison: "Équipement & maison", specialise: "Spécialisé" };

const UNIVERS = [
  {
    id: "grande-distribution", cat: "alimentaire", icon: ShoppingCart,
    title: "Grande distribution alimentaire", subtitle: "Hypermarchés & supermarchés",
    doing: "Mise en rayon en grande quantité, rotation des produits (DLC), facing, balisage des prix, tenue de caisse et préparation de commandes drive.",
    enseignes: ["Carrefour", "E.Leclerc", "Auchan", "Intermarché", "Super U"],
    metiers: ["Employé(e) de libre-service", "Hôte/Hôtesse de caisse", "Préparateur(trice) drive"],
  },
  {
    id: "proximite", cat: "alimentaire", icon: Clock,
    title: "Commerce de proximité", subtitle: "Supérettes & magasins de quartier",
    doing: "Polyvalence totale : réassort, encaissement, accueil de proximité, gestion des petites commandes et des dépannages.",
    enseignes: ["Carrefour City", "Franprix", "Proxi", "Vival", "Casino Shop"],
    metiers: ["Employé(e) polyvalent(e) de commerce", "Vendeur(se)"],
  },
  {
    id: "discount", cat: "alimentaire", icon: Tag,
    title: "Hard discount & bazar", subtitle: "Prix bas, rythme soutenu",
    doing: "Mise en rayon rapide sur palettes, réassort intensif, balisage clair et tenue d'un rythme de travail élevé.",
    enseignes: ["Lidl", "Aldi", "Action", "Normal", "Gifi"],
    metiers: ["Employé(e) de magasin polyvalent(e)", "Hôte/Hôtesse de caisse"],
  },
  {
    id: "alim-specialise", cat: "alimentaire", icon: Croissant,
    title: "Commerce alimentaire spécialisé", subtitle: "Artisans & métiers de bouche",
    doing: "Vente conseil de produits frais, mise en valeur soignée, respect strict de l'hygiène et relation client de proximité.",
    enseignes: ["Boulangerie", "Fromagerie", "Primeur", "Caviste", "Chocolatier"],
    metiers: ["Vendeur(se) en alimentation", "Employé(e) de vente"],
  },
  {
    id: "equip-personne", cat: "maison", icon: Shirt,
    title: "Équipement de la personne", subtitle: "Mode, prêt-à-porter & chaussures",
    doing: "Merchandising mode, cintrage et pliage, gestion des cabines et des tailles, conseil look et vente additionnelle.",
    enseignes: ["Kiabi", "H&M", "Zara", "Gémo", "Chaussea"],
    metiers: ["Vendeur(se) en prêt-à-porter", "Conseiller(ère) de vente"],
  },
  {
    id: "equip-maison", cat: "maison", icon: Sofa,
    title: "Équipement de la maison & déco", subtitle: "Meuble, décoration, art de la table",
    doing: "Mise en scène déco (théâtralisation), conseil produits, gestion de stocks volumineux et présentation d'ambiances.",
    enseignes: ["Maisons du Monde", "IKEA", "Centrakor", "La Foir'Fouille"],
    metiers: ["Vendeur(se)", "Employé(e) de rayon déco"],
  },
  {
    id: "bricolage", cat: "maison", icon: Hammer,
    title: "Bricolage & jardinerie", subtitle: "Outillage, matériaux, plantes",
    doing: "Conseil technique, port de charges, entretien des plantes, balisage de rayons volumineux et orientation des clients.",
    enseignes: ["Leroy Merlin", "Castorama", "Brico Dépôt", "Jardiland", "Truffaut"],
    metiers: ["Conseiller(ère) de vente", "Employé(e) de rayon"],
  },
  {
    id: "sport-culture", cat: "specialise", icon: Trophy,
    title: "Sport, loisirs & culture", subtitle: "Articles de sport, livres, jeux",
    doing: "Conseil produit et essais, mise en avant des nouveautés, gestion de stock et animation du rayon.",
    enseignes: ["Decathlon", "Intersport", "Fnac", "Cultura", "Micromania"],
    metiers: ["Vendeur(se) sport/culture", "Conseiller(ère) de vente"],
  },
  {
    id: "beaute", cat: "specialise", icon: Sparkles,
    title: "Beauté, parfumerie & santé", subtitle: "Parfums, cosmétiques, parapharmacie",
    doing: "Conseil beauté personnalisé, mise en valeur des produits, vente additionnelle et soin de la présentation.",
    enseignes: ["Sephora", "Marionnaud", "Nocibé", "Parapharmacies"],
    metiers: ["Conseiller(ère) de vente beauté", "Vendeur(se)"],
  },
  {
    id: "electro", cat: "specialise", icon: Plug,
    title: "Électroménager & multimédia", subtitle: "High-tech, électroménager",
    doing: "Conseil technique, démonstration de produits, vente de services et garanties, gestion d'un SAV simple.",
    enseignes: ["Boulanger", "Darty", "Fnac", "Electro Dépôt"],
    metiers: ["Vendeur(se) high-tech", "Conseiller(ère) de vente"],
  },
  {
    id: "animalerie", cat: "specialise", icon: PawPrint,
    title: "Animalerie", subtitle: "Produits & accessoires animaux",
    doing: "Conseil clients, soin et entretien des rayons et des animaux, vente d'accessoires et d'alimentation adaptée.",
    enseignes: ["Maxi Zoo", "Animalis", "Jardiland (animalerie)"],
    metiers: ["Vendeur(se) en animalerie", "Conseiller(ère) de vente"],
  },
];

const METIERS = [
  { name: "Employé(e) de libre-service", desc: "Mise en rayon, réassort et tenue des linéaires.", secteur: "Tous secteurs" },
  { name: "Hôte / Hôtesse de caisse", desc: "Encaissement, accueil et fidélisation en caisse.", secteur: "Tous secteurs" },
  { name: "Vendeur(se)", desc: "Conseil client, vente et mise en valeur des produits.", secteur: "Tous secteurs" },
  { name: "Conseiller(ère) de vente", desc: "Vente conseil spécialisée et accompagnement du client.", secteur: "Spécialisé" },
  { name: "Employé(e) de rayon", desc: "Gestion d'un rayon : commandes, balisage, propreté.", secteur: "Tous secteurs" },
  { name: "Équipier(ère) polyvalent(e)", desc: "Polyvalence : rayon, caisse, réserve et accueil.", secteur: "Proximité / GD" },
  { name: "Préparateur(trice) de commandes (Drive)", desc: "Préparation des commandes en ligne et drive.", secteur: "Grande distribution" },
  { name: "Employé(e) de commerce de proximité", desc: "Toutes les tâches d'un magasin de quartier.", secteur: "Proximité" },
  { name: "Vendeur(se) en alimentation", desc: "Vente de produits frais, conseil et hygiène.", secteur: "Alimentaire" },
  { name: "Hôte / Hôtesse d'accueil", desc: "Accueil, orientation et information du client.", secteur: "Tous secteurs" },
  { name: "Vendeur(se)-conseil bricolage/jardin", desc: "Conseil technique sur l'outillage et le végétal.", secteur: "Bricolage & jardinerie" },
  { name: "Vendeur(se) high-tech", desc: "Démonstration et conseil sur les produits techniques.", secteur: "Électroménager & multimédia" },
  { name: "Conseiller(ère) beauté", desc: "Conseil cosmétique et vente additionnelle.", secteur: "Beauté & santé" },
  { name: "Employé(e) e-commerce / drive", desc: "Préparation et expédition des commandes web.", secteur: "Omnicanal" },
];

function Stat({ value, label }) {
  return (
    <div className="rounded-2xl bg-white/70 border border-white/60 px-4 py-3 text-center">
      <p className="font-display text-2xl font-bold text-slate-900">{value}</p>
      <p className="text-[11px] font-extrabold uppercase tracking-wide text-slate-500 mt-0.5">{label}</p>
    </div>
  );
}

export default function PfmpUniversPage() {
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);

  const list = filter === "all" ? UNIVERS : UNIVERS.filter((u) => u.cat === filter);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 mz-rise">
      <Link to="/progression" className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-400 hover:text-slate-600">
        <ArrowLeft className="h-4 w-4" /> Retour à la progression
      </Link>

      <div className="mt-4 flex items-start gap-4">
        <div className="h-12 w-12 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0"><Compass className="h-6 w-6" /></div>
        <div>
          <p className="text-sm font-bold text-emerald-600 uppercase tracking-wide">CAP EPC · Orientation PFMP</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 leading-tight">Où faire ma PFMP ?</h1>
          <p className="text-slate-500 font-medium mt-1">Les univers commerciaux, les enseignes et les métiers accessibles avec le CAP Équipier Polyvalent du Commerce.</p>
        </div>
      </div>

      {/* Intro */}
      <div className="mt-7 rounded-3xl bg-gradient-to-br from-emerald-50 to-sky-50 border border-emerald-100 p-6 sm:p-7">
        <h2 className="font-display text-lg font-semibold text-slate-900">La PFMP, c'est quoi ?</h2>
        <p className="text-sm text-slate-600 font-medium mt-2 leading-relaxed">
          La <strong>Période de Formation en Milieu Professionnel</strong> te place en magasin pour apprendre le métier en situation réelle.
          En 1ʳᵉ année tu découvres et tu réalises les gestes de base ; en 2ᵉ année tu prends en charge un rayon et tu proposes des améliorations.
          Choisis l'univers qui t'attire pour voir ce qu'on y fait et les métiers possibles.
        </p>
        <div className="grid grid-cols-3 gap-3 mt-5">
          <Stat value="14" label="Semaines sur 2 ans" />
          <Stat value="11" label="Univers à explorer" />
          <Stat value="14" label="Métiers visés" />
        </div>
      </div>

      <Tabs defaultValue="univers" className="mt-8">
        <TabsList className="bg-slate-100 rounded-full p-1 h-auto">
          <TabsTrigger value="univers" data-testid="tab-univers" className="rounded-full px-4 py-2 font-bold data-[state=active]:bg-white data-[state=active]:text-emerald-600"><Store className="h-4 w-4 mr-1.5" /> Les univers</TabsTrigger>
          <TabsTrigger value="metiers" data-testid="tab-metiers" className="rounded-full px-4 py-2 font-bold data-[state=active]:bg-white data-[state=active]:text-emerald-600"><Users className="h-4 w-4 mr-1.5" /> Les métiers</TabsTrigger>
        </TabsList>

        <TabsContent value="univers" className="mt-6">
          <div className="flex flex-wrap gap-2 mb-6">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                onClick={() => setFilter(c.id)}
                data-testid={`univers-filter-${c.id}`}
                className={`px-4 h-9 rounded-full text-sm font-bold transition-colors ${filter === c.id ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
              >
                {c.label}
              </button>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {list.map((u) => {
              const s = CAT_STYLE[u.cat];
              const Icon = u.icon;
              return (
                <button
                  key={u.id}
                  onClick={() => setActive(u)}
                  data-testid={`univers-card-${u.id}`}
                  className={`group text-left rounded-3xl bg-white border border-slate-100 shadow-sm p-5 transition-all hover:shadow-md ${s.ring}`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`inline-flex items-center justify-center h-11 w-11 rounded-2xl ${s.ic}`}><Icon className="h-5 w-5" /></span>
                    <span className={`text-[10px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-full ${s.chip}`}>{CAT_LABEL[u.cat]}</span>
                  </div>
                  <h3 className="font-display text-base font-semibold text-slate-900 mt-4 leading-snug">{u.title}</h3>
                  <p className="text-sm text-slate-500 font-medium mt-1">{u.subtitle}</p>
                  <span className="inline-flex items-center gap-1.5 mt-4 text-sm font-bold text-emerald-600 group-hover:gap-2.5 transition-all">
                    Découvrir <ArrowRight className="h-4 w-4" />
                  </span>
                </button>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="metiers" className="mt-6">
          <div className="grid sm:grid-cols-2 gap-4">
            {METIERS.map((m) => (
              <div key={m.name} data-testid={`metier-card-${m.name}`} className="rounded-3xl bg-white border border-slate-100 shadow-sm p-5 flex items-start gap-3">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-2xl bg-sky-100 text-sky-600 shrink-0"><Briefcase className="h-5 w-5" /></span>
                <div>
                  <h3 className="font-display text-base font-semibold text-slate-900 leading-snug">{m.name}</h3>
                  <p className="text-sm text-slate-500 font-medium mt-1">{m.desc}</p>
                  <span className="inline-flex items-center gap-1 mt-2 text-[11px] font-extrabold uppercase tracking-wide text-slate-400"><MapPin className="h-3 w-3" /> {m.secteur}</span>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Detail dialog */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="rounded-3xl max-w-lg max-h-[88vh] overflow-y-auto">
          {active && (() => {
            const s = CAT_STYLE[active.cat];
            const Icon = active.icon;
            return (
              <div>
                <DialogHeader>
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center justify-center h-12 w-12 rounded-2xl ${s.ic}`}><Icon className="h-6 w-6" /></span>
                    <div>
                      <span className={`text-[10px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-full ${s.chip}`}>{CAT_LABEL[active.cat]}</span>
                      <DialogTitle className="font-display text-xl mt-1">{active.title}</DialogTitle>
                    </div>
                  </div>
                </DialogHeader>

                <p className="text-sm font-bold text-slate-400 mt-1">{active.subtitle}</p>

                <div className="mt-5">
                  <p className="text-[11px] font-extrabold uppercase tracking-wide text-emerald-600">Ce que tu y fais</p>
                  <p className="text-sm text-slate-600 font-medium mt-1 leading-relaxed">{active.doing}</p>
                </div>

                <div className="mt-5">
                  <p className="text-[11px] font-extrabold uppercase tracking-wide text-emerald-600 flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" /> Exemples d'enseignes</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {active.enseignes.map((e) => (
                      <span key={e} className="text-xs font-bold text-slate-600 bg-slate-100 rounded-full px-3 py-1">{e}</span>
                    ))}
                  </div>
                </div>

                <div className="mt-5">
                  <p className="text-[11px] font-extrabold uppercase tracking-wide text-emerald-600 flex items-center gap-1.5"><GraduationCap className="h-3.5 w-3.5" /> Métiers visés</p>
                  <ul className="mt-2 space-y-1.5">
                    {active.metiers.map((m) => (
                      <li key={m} className="flex items-center gap-2 text-sm font-medium text-slate-700">
                        <Briefcase className="h-4 w-4 text-sky-500 shrink-0" /> {m}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
